import React, { memo, useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useRecoilValue } from 'recoil';
import format from 'date-fns/format';
import { IconButton, Image, Spinner } from 'components';
import Tooltip from 'components/Tooltip';
import { renderToasterWithTickmark } from 'components/layouts/Header/Header.component';
import Avatar from 'components/Avatar';
import { useCommon } from 'hooks/useCommon';
import { useNotification } from 'hooks/useNotification';
import { TouchpointAttributes } from 'lib/models/message';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { parseResponse } from 'lib/utils/parser';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { EventTouchpointable } from 'lib/models/event';
import { useCandidateMessage } from 'lib/providers/CandidateMessageProvider';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from './TouchpointMessage.module.scss';

export interface TouchpointMessageProps {
  isSender: boolean;
  /** touchpointData is of type TouchpointAttributes
   * when attached opportunity id is sent
   * touchpointData is of type OpportunityAttributes
   * when links are present, so have added the reqd fields
   * as optional on TouchpointAttributes in message.ts
   */
  touchpointData: TouchpointAttributes;
  showSave?: boolean;
  mutateMessages?: () => void;
  showOnRight?: boolean;
  isLoading?: boolean;
  link?: string[];
  senderIcon?: string;
  senderName?: string;
  messageType?: string | null;
}

export const TouchpointMessage: React.FC<TouchpointMessageProps> = memo(
  ({
    isSender,
    touchpointData,
    showSave,
    mutateMessages,
    link,
    showOnRight = false,
    isLoading,
    senderIcon,
    senderName,
    messageType,
  }: TouchpointMessageProps) => {
    const { fetchTouchpointData } = useCandidateMessage();

    const {
      start_date,
      slug,
      touchpointable,
      id,
      touchpointable_type,
      title,
      creatable_for,
      following: followingResponse,
      // we get the below fields when attached opportunity id is sent
      icon_url: icon_url_data,
      creatable_for_name,
      is_saved,
      following_id: following_id_data,
    } = touchpointData || {};

    const { icon_url: event_icon_url } =
      parseResponse(touchpointable as EventTouchpointable) || {};
    const icon_url = icon_url_data || event_icon_url;

    const following = parseResponse(followingResponse);

    const { followEntity, unfollowEntity } = useCommon();
    const { headers } = useRecoilValue(httpHeadersState);
    const [isFollowing, setIsFollowing] = useState(false);
    const notificationInstance = useNotification();
    const createdByData = parseResponse<
      CompanyAttributes | StudentSocietyAttributes
    >(creatable_for);

    useEffect(() => {
      setIsFollowing(!!following || is_saved);
    }, [following, is_saved]);

    const onPreviewClick = () => {
      if (slug) {
        const publicPageUrl = `${
          window.location.origin
        }${getOpportunityPublicPagePathname(touchpointable_type, slug || '')}`;
        window.open(publicPageUrl, '_blank');
      }
    };

    const onFollowHandleClick = async () => {
      const { id: followingId } = following || {};
      const following_id = is_saved ? following_id_data : followingId;

      if (isFollowing && following_id) {
        const response = await unfollowEntity({
          id: following_id,
          headers,
        });
        if (response) {
          setIsFollowing(false);
          notificationInstance.custom({
            message: '',
            renderContent: () =>
              renderToasterWithTickmark('Opportunity unsaved'),
          });
        }
      } else {
        const response = await followEntity({
          followable_type: 'Touchpoint',
          followable_id: id,
          headers,
        });
        if (response) {
          setIsFollowing(true);
          notificationInstance.custom({
            message: '',
            renderContent: () => renderToasterWithTickmark('Opportunity saved'),
          });
        }
      }
      if (mutateMessages) {
        mutateMessages();
      } else {
        fetchTouchpointData(slug);
      }
    };

    const followIcon = useMemo(() => {
      return isFollowing ? 'icon_bookmark-filled' : 'icon_bookmark';
    }, [isFollowing]);

    const startDateTimeValue = start_date
      ? format(new Date(start_date), 'MMM d, h:mma')
      : null;

    return (
      <div
        className={cn(styles.opportunitySection, {
          [styles.messageOut]: showOnRight,
        })}
        onClick={onPreviewClick}
      >
        {messageType == 'Touchpoint' && !showOnRight ? (
          <Avatar size="small" icon={senderIcon} name={senderName || ''} />
        ) : null}
        <div
          className={cn(styles.opportunitySectionCard, {
            [styles.opportunitySectionCardSecondary]: isSender,
          })}
        >
          {isLoading ? (
            <Spinner
              size="xsmall"
              color={showOnRight ? 'secondary' : 'primary'}
            />
          ) : (
            <>
              <div
                className={cn(styles.opportunitySectionCardImageWrapper, {
                  [styles.opportunitySectionCardImageWrapperEvent]:
                    touchpointable_type === 'Event',
                })}
              >
                {!createdByData?.icon_url && !icon_url ? (
                  <Avatar
                    name={createdByData?.name}
                    className={styles.avatar}
                    size="small"
                  />
                ) : (
                  <Image
                    src={
                      link && link?.length > 0
                        ? createdByData?.icon_url
                        : icon_url
                    }
                  />
                )}
              </div>
              <div className={cn(styles.opportunitySectionCardContent)}>
                {touchpointable_type === 'Event' && startDateTimeValue ? (
                  <p className={styles.opportunitySectionCardContentDate}>
                    {startDateTimeValue}
                  </p>
                ) : null}
                <p className={styles.opportunitySectionCardContentTitle}>
                  {title}
                </p>
                <div
                  className={styles.opportunitySectionCardContentDescription}
                >
                  <p>{touchpointable_type}</p>
                  <p>•</p>
                  <p>
                    {link && link?.length > 0
                      ? createdByData?.name
                      : creatable_for_name}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        {!isLoading && (
          <div className={styles.opportunitySectionIcons}>
            <Tooltip
              content="View"
              position="top"
              xAlign="center"
              color="dark"
              className={styles.tooltipContainer}
            >
              <IconButton
                iconName="icon_launch"
                onClick={onPreviewClick}
                className={styles.opportunitySectionIconsItem}
              />
            </Tooltip>
            {showSave ? (
              <Tooltip
                content="Save"
                position="top"
                xAlign="center"
                color="dark"
                className={styles.tooltipContainer}
                containerClassName={styles.tooltipContainerContent}
              >
                <IconButton
                  key={followIcon}
                  iconName={followIcon}
                  onClick={onFollowHandleClick}
                  className={styles.opportunitySectionIconsItem}
                />
              </Tooltip>
            ) : null}
          </div>
        )}
      </div>
    );
  }
);

TouchpointMessage.displayName = 'TouchpointMessage';
